import React, { useState, useRef } from 'react';
import { Search, X, Check, Volume2, Play, Pause } from 'lucide-react';
import './SfxPickerModal.css';

/**
 * SfxPickerModal - Modal for selecting sound effects to attach to words
 */
const SfxPickerModal = ({
    isOpen,
    onClose,
    onSelect,
    sfxLibrary = [],
    currentSfx = null
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSfx, setSelectedSfx] = useState(currentSfx);
    const [playingId, setPlayingId] = useState(null);
    const audioRef = useRef(null);

    if (!isOpen) return null;

    // Filter SFX by search query
    const filteredSfx = sfxLibrary.filter(sfx =>
        sfx.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sfx.filename?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Play/stop SFX preview
    const handlePlayPreview = (sfx, e) => {
        e.stopPropagation();

        if (playingId === sfx.id) {
            // Stop playing
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
            setPlayingId(null);
        } else {
            // Start playing
            if (audioRef.current) {
                audioRef.current.src = sfx.url;
                audioRef.current.play().catch(() => { });
                setPlayingId(sfx.id);
            }
        }
    };

    // Handle audio ended
    const handleAudioEnded = () => {
        setPlayingId(null);
    };

    // Select SFX
    const handleSelect = (sfx) => {
        setSelectedSfx(sfx);
    };

    // Confirm selection
    const handleConfirm = () => {
        if (!selectedSfx) return;
        onSelect({
            id: selectedSfx.id,
            url: selectedSfx.url,
            label: selectedSfx.label,
            volume: 1.0
        });
    };

    // Remove SFX (select null)
    const handleRemove = () => {
        onSelect(null);
    };

    return (
        <div className="sfx-picker-overlay" onClick={onClose}>
            <audio ref={audioRef} onEnded={handleAudioEnded} style={{ display: 'none' }} />

            <div className="sfx-picker-modal" onClick={e => e.stopPropagation()}>
                <div className="sfx-picker-header">
                    <h3><Volume2 size={20} /> Select Sound Effect</h3>
                    <button className="close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Search */}
                <div className="sfx-picker-search">
                    <Search size={16} />
                    <input
                        type="text"
                        placeholder="Search sound effects..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                </div>

                {/* SFX Grid */}
                <div className="sfx-picker-content">
                    <div className="sfx-grid">
                        {filteredSfx.length === 0 ? (
                            <p className="no-sfx">
                                {sfxLibrary.length === 0
                                    ? 'No sound effects available'
                                    : 'No sound effects match your search'}
                            </p>
                        ) : (
                            filteredSfx.map(sfx => (
                                <div
                                    key={sfx.id}
                                    className={`sfx-item ${selectedSfx?.id === sfx.id ? 'selected' : ''}`}
                                    onClick={() => handleSelect(sfx)}
                                >
                                    <button
                                        className="sfx-play-btn"
                                        onClick={(e) => handlePlayPreview(sfx, e)}
                                    >
                                        {playingId === sfx.id ? (
                                            <Pause size={16} />
                                        ) : (
                                            <Play size={16} />
                                        )}
                                    </button>
                                    <span className="sfx-label">{sfx.label}</span>
                                    {selectedSfx?.id === sfx.id && (
                                        <Check size={16} className="check-icon" />
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="sfx-picker-footer">
                    {currentSfx && (
                        <button className="remove-btn" onClick={handleRemove}>
                            Remove SFX
                        </button>
                    )}
                    <div className="footer-right">
                        <button className="cancel-btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className="confirm-btn"
                            onClick={handleConfirm}
                            disabled={!selectedSfx}
                        >
                            <Check size={16} /> Select
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SfxPickerModal;
